import {sortBy} from 'lodash-es';
import {v4 as uuidv4} from 'uuid';
import {ISimpleScheduleEntry, worker} from '@pluto-tv/assemble';

import {ITimeline} from 'features/channelTimelines/channelTimelinesApi';
import {getLuxonObject, areDatesInDifferentDays} from 'utils/dateUtils';
import {ChannelCatalogItemWithTimeline, IChannelCatalogItem} from 'models/channelCatalog';
import {isSimpleScheduleEntry} from 'views/programming/channel/edit/program/components/EpisodesDetails';

import {SelectMode} from '.';

import {IStateMsgList, StateColorType, getItemState} from '../../utils';
import {ChannelCatalogItemWithState as IChannelCatalogItemWithState} from '../../contexts/getMemoryListProvider';

export const SAFE_PUBLISHED_WINDOW_IN_SECONDS = 1800;

const toSec = (d: Date) => Math.floor(d.getTime() / 1000);

/**
 * Normalizes a date by removing milliseconds to ensure consistent time comparisons.
 * Truncates on the instant (epoch) rather than via setMilliseconds(): the local-time
 * setter collapses the DST fall-back repeated hour to its earlier occurrence, shifting
 * the instant back 1h and inverting start/stop.
 * @param date - The date to normalize
 * @returns A new Date object with milliseconds set to 0
 */
export const normalizeDate = (date: Date): Date => new Date(Math.floor(date.getTime() / 1000) * 1000);

export const getMinStart = (): Date => {
  const now = new Date();
  return new Date(now.setSeconds(now.getSeconds() + SAFE_PUBLISHED_WINDOW_IN_SECONDS));
};

export const isLockedSlot = (
  timestamp: number | Date,
  options?: {gracenoteEnabled?: boolean; cfaasEnabled?: boolean},
): boolean => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return date < getMinStart() || !!options?.gracenoteEnabled || !!options?.cfaasEnabled;
};

export const getDraftTimeline = (
  drafts: ITimeline[],
  published: ITimeline[],
): {
  events: ITimeline[];
  borderEvent: ITimeline | null;
} => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  const currentPoint = getLuxonObject(new Date() as Date)
    ?.plus({minutes: 30})
    .toJSDate()!;

  // Show all drafts sorted by start date (the draft view should display what's programmed, not what was transmitted)
  const sortedEvents = [...drafts].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // Keep borderEvent calculation for locked state (episodes within 30min window should remain locked)
  const pubEvents = published.filter(p => new Date(p.start) <= currentPoint);

  return {
    events: sortedEvents,
    borderEvent: pubEvents.length >= 1 ? pubEvents[pubEvents.length - 1] : null,
  };
};

/**
 * Fills the locked area (entries before `now + 30 min`) of the draft display
 * from the published view. Invoked unconditionally on every draft render to
 * prevent a visual gap when the backend strips locked-area entries (e.g.
 * after a Discard Draft, which persists and would otherwise leave the gap
 * visible after a page reload). Any draft entry that already starts at the
 * same instant as a published entry is preserved (published is skipped), so
 * a partially-stripped draft is filled without duplicates.
 *
 * Display only — the result is NOT stored back into programModel.draft.
 */
export const supplementDraftWithPublishedLocked = (
  draftTimelines: ITimeline[],
  publishedTimelines: ITimeline[],
): ITimeline[] => {
  const minStart = getMinStart();
  const draftStartTimes = new Set(draftTimelines.map(d => normalizeDate(new Date(d.start)).getTime()));

  const supplements = publishedTimelines
    .filter(pub => {
      const start = normalizeDate(new Date(pub.start));
      return start < minStart && !draftStartTimes.has(start.getTime());
    })
    .map(pub => ({
      ...pub,
      start: normalizeDate(new Date(pub.start)),
      stop: normalizeDate(new Date(pub.stop)),
      locked: true,
    }));

  if (!supplements.length) return draftTimelines;

  return [...supplements, ...draftTimelines].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
};

export interface ITimeframe {
  start: Date;
  stop: Date;
}

const mapToSimpleScheduleEntry = (episode: IChannelCatalogItem): ISimpleScheduleEntry => {
  return {
    title: episode.series?.name || '',
    subTitle: episode.name,
    episodeId: episode.id,
    duration: episode.duration,
    id: episode.id,
    minHeight: '8.15rem',
    start: new Date(),
    ...getTimelineState(episode),
  };
};

export const mapToSimpleScheduleEntries = (
  episodes: (IChannelCatalogItem | ISimpleScheduleEntry)[],
): ISimpleScheduleEntry[] => {
  return episodes.map(episode => {
    return isSimpleScheduleEntry(episode) ? episode : mapToSimpleScheduleEntry(episode);
  });
};

export const isFirstTimeAired = (timeline: ITimeline): boolean => {
  const today = new Date();
  const {
    start,
    episode: {firstAired},
  } = timeline;

  if (!start || firstAired === '') return false;

  const startTime = new Date(start);
  const firstAiredTime = new Date(firstAired);

  return startTime.getTime() === firstAiredTime.getTime() && startTime > today;
};

export const mapTimelinesIntoTimelinesEntry = (
  timelines: ITimeline[],
  isGracenoteReady: boolean,
  draggablePermission = false,
): ISimpleScheduleEntry[] => {
  return timelines.map(timeline => {
    const draft: ISimpleScheduleEntry = {
      title: timeline.episode.series?.name || '',
      subTitle: timeline.episode.name,
      episodeId: timeline.episode.id,
      ...timeline,
      ...timeline.episode,
      id: timeline.id,
      state: timeline.state as StateColorType,
      minHeight: '8.15rem',
      draggable: draggablePermission,
    };

    if (timeline.locked) {
      delete draft.stateMsgList;
      delete draft.state;
    } else {
      const firstTime = isFirstTimeAired(timeline);

      if (timeline.stateMsgList && timeline.stateMsgList?.length > 0) {
        draft.errorMessagesWithState = timeline.stateMsgList.map(state => ({
          state: state.color as StateColorType,
          message: state.label,
        }));
      } else if (firstTime) {
        draft.state = 'success' as StateColorType;
      }

      if (firstTime) {
        draft.errorMessagesWithState = [
          ...(draft.errorMessagesWithState || []),
          {
            state: 'info',
            message: 'Episode first time airing on a channel.',
          },
        ];
      }
    }

    if (timeline.episode?.published) {
      if (isGracenoteReady) {
        draft.manualOverrideMarker = new Date();
      } else if (draft.manualOverrideMarker) {
        delete draft.manualOverrideMarker;
      }
    }
    return draft;
  });
};

export const canUseEpisodeForTimeline = (episodeToAssign: IChannelCatalogItem, start: Date): boolean =>
  episodeToAssign.hasLinearAvailabilityWindows &&
  episodeToAssign.allClipsReady &&
  episodeToAssign.isAvailableNow &&
  !!episodeToAssign.availabilityWindows.linear.find(
    currentWindow => start >= new Date(currentWindow.startDate) && start <= new Date(currentWindow.endDate),
  );

export const getTimelineState = (
  item: IChannelCatalogItem | ITimeline,
): {state: any; stateMsgList: IStateMsgList[]} => {
  const obj: IChannelCatalogItem = {...(item as IChannelCatalogItem)};

  if (
    ('providerIsJwMixed' in item && !('hasMixedProviderWith' in item)) ||
    ('providerIsJwOnly' in item && !('hasUniqueProvider' in item))
  ) {
    obj.hasMixedProviderWith = !!item.providerIsJwMixed;
    obj.hasUniqueProvider = !!item.providerIsJwOnly;
  }

  if ('hasEmptySources' in item && !('hasClipWithEmptySources' in item)) {
    obj.hasClipWithEmptySources = !!item.hasEmptySources;
  }

  // warnings or errors
  const stateResult = getItemState(obj as ChannelCatalogItemWithTimeline);
  return stateResult;
};

export const getTimeLineEpisode = (
  episode: ISimpleScheduleEntry | IChannelCatalogItem,
  timeline: ITimeline[],
  start: Date,
): ITimeline | null => {
  if (!episode) return null;

  const uniqueId = uuidv4();

  if (!isSimpleScheduleEntry(episode)) {
    return {
      id: `${episode.id}-${uniqueId}`,
      start,
      stop: new Date(start.getTime() + episode.allotment * 1000),
      episode: episode,
      ...getTimelineState(episode),
    } as ITimeline;
  } else {
    const timelineEpisode = timeline.find(t => t.id === episode.id);
    if (!timelineEpisode) return null;

    const episodeId = timelineEpisode.episode.id;
    const newId = `${episodeId}-${uniqueId}`;

    return {
      ...timelineEpisode,
      id: newId,
      locked: false,
      start,
      stop: new Date(start.getTime() + timelineEpisode.stop.getTime() - timelineEpisode.start.getTime()),
    };
  }
};

export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const recalculateEventTimes = (
  episodes: (IChannelCatalogItem | ISimpleScheduleEntry)[],
  timeline: ITimeline[],
  startDate: Date,
  mode: SelectMode = 'vertical-select',
): ITimeline[] => {
  const isAllFromCalendar = !episodes.some(a => !isSimpleScheduleEntry(a));
  const updatedMode = mode === 'group-select' && !isAllFromCalendar ? 'vertical-select' : mode;

  if (updatedMode === 'group-select') {
    const sorted = sortBy(episodes as ISimpleScheduleEntry[], 'start');

    let timeDifference = 0;

    return sorted.reduce((acc, episode, index) => {
      const start: Date = (() => {
        if (index === 0) {
          timeDifference = startDate.getTime() - new Date((sorted[0] as ISimpleScheduleEntry).start).getTime();

          // For the first episode, always use the provided startDate
          return startDate;
        }

        const episodeObj = sorted[index] as ISimpleScheduleEntry;
        const episodeStart = new Date(episodeObj.start).getTime();

        // Add the same time difference to the new start time
        return new Date(episodeStart + timeDifference);
      })();

      const timelineEpisode = getTimeLineEpisode(episode, timeline, start);
      return timelineEpisode ? [...acc, timelineEpisode] : acc;
    }, [] as ITimeline[]);
  } else {
    return episodes.reduce((acc, episode, index) => {
      const start: Date = acc[index - 1]
        ? updatedMode === 'vertical-select'
          ? acc[index - 1].stop
          : addDays(acc[index - 1].start, index === 0 ? 0 : 1)
        : startDate;
      const timelineEpisode = getTimeLineEpisode(episode, timeline, start);

      return timelineEpisode ? [...acc, timelineEpisode] : acc;
    }, [] as ITimeline[]);
  }
};

/**
 * This function moves the start and stop times of the events in the timeline to the new start time.
 * It ensures episodes are moved forward to avoid overlaps, normalizing times for consistent comparison.
 * @param timeline - The timeline to update
 * @param start - The new start time
 * @returns The updated timeline
 */
const moveTimes = (timeline: ITimeline[], start: Date): ITimeline[] => {
  // Normalize the start time to ensure consistent comparison
  let currentStop = normalizeDate(start); // Track where the previous episode ended

  return timeline.map((timelineEvent, index) => {
    // Calculate the event duration
    const eventDuration = timelineEvent.episode.allotment * 1000;

    // Normalize the event's start time for comparison
    const normalizedEventStart = normalizeDate(timelineEvent.start);

    // For the first event, always place it at currentStop (which is the normalized start)
    // For subsequent events, check if they need to be moved
    let newStart: Date;
    let newStop: Date;

    if (index === 0) {
      // First event: always place it at the start position
      newStart = currentStop;
      newStop = new Date(newStart.getTime() + eventDuration);
    } else {
      // Subsequent events: check if they overlap with the previous episode
      // An overlap occurs if: normalizedEventStart < currentStop
      // (if they start at the same time or before the previous ends, there's overlap)
      if (normalizedEventStart < currentStop) {
        // There's overlap - move it forward to start right after the previous episode ends
        newStart = currentStop;
        newStop = new Date(newStart.getTime() + eventDuration);
      } else {
        // No overlap - the event starts after or at the same time as currentStop
        // If it starts exactly at currentStop, we still keep it sequential by using currentStop
        // If it starts after, there's a gap - keep it in place
        if (normalizedEventStart === currentStop) {
          // Starts exactly when previous ends - keep sequential
          newStart = currentStop;
          newStop = new Date(newStart.getTime() + eventDuration);
        } else {
          // There's a gap - keep it in place
          newStart = timelineEvent.start;
          newStop = timelineEvent.stop;
        }
      }
    }

    // Create the event with the updated start and stop times
    const movedEvent = {
      ...timelineEvent,
      start: newStart,
      stop: newStop,
    };

    // Update currentStop for the next event to be the stop time of the current event
    // Normalize to ensure consistency
    currentStop = normalizeDate(newStop);

    return movedEvent;
  });
};

/**
 * This function moves the start and stop times of the events up in the calendar to the new start time.
 * @param timeline - The timeline to update
 * @param start - The new start time
 * @returns The updated timeline
 */
export const moveTimesUp = (timeline: ITimeline[], start: Date): ITimeline[] => {
  let newStart = start;

  return timeline
    .sort((a, b) => (a.start.getTime() > b.start.getTime() ? 1 : -1))
    .map(timelineEvent => {
      // If the event's start time is later than the new start time + 30min return the event as is
      if (timelineEvent.start.getTime() > newStart.getTime() + 30 * 60 * 1000) {
        newStart = timelineEvent.stop;
        return timelineEvent;
      }

      // Calculate the new stop time for the event, maintaining the original duration
      const newStop = new Date(newStart.getTime() + timelineEvent.episode.allotment * 1000);

      // Create a new event with the updated start and stop times
      const newTimeline = {
        ...timelineEvent,
        start: newStart,
        stop: newStop,
      };

      // Update the new start time for the next event to be the stop time of the current event
      newStart = newStop;

      return newTimeline;
    });
};

/**
 * This function adds new episodes to the timeline and returns the updated timeline.
 * It handles episodes with milliseconds by normalizing times and detecting/replacing overlaps
 * to ensure episodes remain linear (no gaps or overlaps).
 * Episodes that have already been transmitted (before now) are not reordered.
 * @param newEpisodes - The new episodes to add to the timeline
 * @param draftTimeline - The current timeline
 * @param hasTmsid - If true, prevents reordering of episodes
 * @param now - Optional date for "now" (for testing purposes)
 * @returns The updated timeline
 */
export const addEpisodes = (
  newEpisodes: ITimeline[],
  draftTimeline: ITimeline[],
  hasTmsid?: boolean,
  now?: Date, // Allow injecting "now" for testing purposes
): ITimeline[] => {
  if (!newEpisodes.length) return draftTimeline;

  const currentTime = now || new Date();

  // If tmsid is present, don't reorder existing episodes, but adjust new episodes to avoid overlaps
  if (hasTmsid) {
    // Sort existing timeline by start time
    const sortedExisting = [...draftTimeline].sort((a, b) => a.start.getTime() - b.start.getTime());

    // Build a combined timeline that includes existing episodes and will include adjusted new episodes
    // This allows us to check for overlaps with both existing and previously added new episodes
    const allEpisodes = [...sortedExisting];

    // Process each new episode to avoid overlaps
    newEpisodes.forEach(newEp => {
      // Only adjust episodes from "now" onwards
      if (newEp.start < currentTime) {
        allEpisodes.push(newEp);
        return;
      }

      let adjustedStart = normalizeDate(newEp.start);
      const duration = newEp.episode.allotment * 1000;
      let adjustedStop = normalizeDate(new Date(adjustedStart.getTime() + duration));

      // Sort all episodes to check for overlaps in order
      const sortedAll = [...allEpisodes].sort((a, b) => a.start.getTime() - b.start.getTime());

      // Check for overlaps with all episodes (existing + previously adjusted new ones)
      for (const existing of sortedAll) {
        const existingStart = normalizeDate(existing.start);
        const existingStop = normalizeDate(existing.stop);

        // Check if there's an overlap: new episode starts before existing ends AND new episode ends after existing starts
        const hasOverlap = adjustedStart < existingStop && adjustedStop > existingStart;

        if (hasOverlap) {
          // Move new episode to start right after the existing one
          adjustedStart = existingStop;
          adjustedStop = normalizeDate(new Date(adjustedStart.getTime() + duration));
        }
      }

      allEpisodes.push({
        ...newEp,
        start: adjustedStart,
        stop: adjustedStop,
      });
    });

    return allEpisodes.sort((a, b) => a.start.getTime() - b.start.getTime());
  }

  // Separate episodes before and after "now" - episodes already transmitted should not be reordered
  const episodesBeforeNow = draftTimeline.filter(draft => draft.start < currentTime);
  const episodesAfterNow = draftTimeline
    .filter(draft => draft.start >= currentTime)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  let newTimelineAfterNow = [...episodesAfterNow];

  newEpisodes.forEach(item => {
    // If the new episode is before "now", just add it without reordering
    if (item.start < currentTime) {
      episodesBeforeNow.push(item);
      episodesBeforeNow.sort((a, b) => a.start.getTime() - b.start.getTime());
      return;
    }

    // Normalize the new episode's start time for consistent comparison
    const normalizedItemStart = normalizeDate(item.start);

    // Find the index where the new episode should be inserted
    const indexToInsert = newTimelineAfterNow.findIndex(draft => toSec(draft.start) >= toSec(normalizedItemStart));

    // If the new episode should be inserted at the end of the timeline
    if (indexToInsert === -1) {
      const lastEvent = newTimelineAfterNow.length ? newTimelineAfterNow[newTimelineAfterNow.length - 1] : null;
      let startDateItem: Date;

      if (lastEvent) {
        // Normalize (truncate milliseconds) from the previous episode's stop time
        const normalizedLastStop = normalizeDate(lastEvent.stop);

        // Update the last event in the timeline with normalized stop time
        const lastEventIndex = newTimelineAfterNow.length - 1;
        newTimelineAfterNow[lastEventIndex] = {
          ...lastEvent,
          stop: normalizedLastStop,
        };

        // If the requested start time is after the previous episode ends, use it (there's a gap)
        // Otherwise, start right after the previous episode ends to avoid overlap
        startDateItem = normalizedItemStart > normalizedLastStop ? normalizedItemStart : normalizedLastStop;
      } else {
        startDateItem = normalizedItemStart;
      }

      // Create a copy of the item with the correct start time to ensure it's placed correctly
      const itemToInsert = {
        ...item,
        start: startDateItem,
      };
      newTimelineAfterNow = [...newTimelineAfterNow, ...moveTimes([itemToInsert], startDateItem)];
    } else {
      // Split the timeline into two parts at the insertion index
      const firstPart = newTimelineAfterNow.slice(0, indexToInsert);
      const lastFirstPartEvent = firstPart.length ? firstPart[firstPart.length - 1] : null;

      // Determine the start date for the new episode
      let startDateItem: Date;

      if (!lastFirstPartEvent || areDatesInDifferentDays(lastFirstPartEvent.stop, normalizedItemStart)) {
        // If the new episode starts on a different day, use the normalized start time
        startDateItem = normalizedItemStart;
      } else {
        // Normalize (truncate milliseconds) from the previous episode's stop time
        const normalizedLastStop = normalizeDate(lastFirstPartEvent.stop);

        // Update the last event in the first part with normalized stop time
        const lastFirstPartIndex = firstPart.length - 1;
        firstPart[lastFirstPartIndex] = {
          ...lastFirstPartEvent,
          stop: normalizedLastStop,
        };

        // If the requested start time is after the previous episode ends, use it (there's a gap)
        // Otherwise, start right after the previous episode ends to avoid overlap
        startDateItem = normalizedItemStart > normalizedLastStop ? normalizedItemStart : normalizedLastStop;
      }

      // Always use moveTimes to insert the new episode and move subsequent episodes forward
      // This ensures that episodes following the insertion point are moved to make space
      // moveTimes will handle the positioning and avoid overlaps by moving episodes forward
      // Create a copy of the item with the correct start time to ensure it's placed correctly
      const itemToInsert = {
        ...item,
        start: startDateItem,
      };
      const secondPart = moveTimes([itemToInsert, ...newTimelineAfterNow.slice(indexToInsert)], startDateItem);
      newTimelineAfterNow = [...firstPart, ...secondPart];
    }
  });

  // Normalize all episodes after "now" (remove milliseconds) from all episodes in the timeline
  // This ensures clean times without milliseconds and prevents overlap issues
  const normalizedTimelineAfterNow = newTimelineAfterNow.map(episode => ({
    ...episode,
    start: normalizeDate(episode.start),
    stop: normalizeDate(episode.stop),
  }));

  // Re-sort and ensure episodes are sequential without overlaps
  const sortedTimelineAfterNow = normalizedTimelineAfterNow.sort((a, b) => a.start.getTime() - b.start.getTime());

  // Ensure all episodes after "now" are sequential (each starts when the previous ends)
  const sequentialTimelineAfterNow: ITimeline[] = [];
  sortedTimelineAfterNow.forEach((episode, index) => {
    if (index === 0) {
      sequentialTimelineAfterNow.push(episode);
    } else {
      const previousEpisode = sequentialTimelineAfterNow[sequentialTimelineAfterNow.length - 1];
      const previousStop = normalizeDate(previousEpisode.stop);
      const currentStart = normalizeDate(episode.start);

      // If current episode starts before or at the same time as previous ends, adjust it
      if (currentStart <= previousStop) {
        const duration = episode.episode.allotment * 1000;
        sequentialTimelineAfterNow.push({
          ...episode,
          start: previousStop,
          stop: normalizeDate(new Date(previousStop.getTime() + duration)),
        });
      } else {
        sequentialTimelineAfterNow.push(episode);
      }
    }
  });

  // Combine episodes before and after "now"
  return [...episodesBeforeNow, ...sequentialTimelineAfterNow].sort((a, b) => a.start.getTime() - b.start.getTime());
};

export const getStartTimeToDrop = (items: ISimpleScheduleEntry[], timeToDrop?: number): number | null => {
  if (timeToDrop) return timeToDrop;
  if (!items.length) return null;

  const lastItem = items[items.length - 1];
  const nowPlus30 = new Date().getTime() + 30 * 60 * 1000;
  const newStartTime: number | null = lastItem.stop >= nowPlus30 ? lastItem.stop : null;
  return newStartTime;
};

/**
 * This function snap up the episodes when they are <= 30 minutes apart
 * @param draftTimeline - The current timeline
 * @param startDate
 * @param isSnapOn
 * @param hasTmsid - If true, prevents reordering of episodes
 * @returns The updated timeline
 */
export const sanitizeDraftEventList = (
  draftTimelines: ITimeline[],
  startDate: Date,
  isSnapOn: boolean,
  hasTmsid?: boolean,
  now?: Date, // Allow injecting "now" for testing purposes
): ITimeline[] => {
  // If tmsid is present, don't reorder episodes
  if (hasTmsid) {
    return draftTimelines;
  }

  if (!isSnapOn) return draftTimelines;

  const currentTime = now || new Date();

  // Filter the draft list with the events of the current week, but only reorder from "now" onwards
  // Episodes that have already been transmitted (before now) should not be reordered
  const currentWeekDraftTimelines: ITimeline[] = draftTimelines
    .filter(draft => startDate && draft.stop >= startDate && draft.start >= currentTime)
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // Episodes before "now" should not be reordered
  const episodesBeforeNow = draftTimelines.filter(draft => draft.start < currentTime);

  if (currentWeekDraftTimelines.length <= 1) {
    return draftTimelines;
  }

  // Keep the above filtered out items (episodes before startDate but after now)
  let firstPart: ITimeline[] = [];

  const allEpisodesAfterNow = draftTimelines.filter(draft => draft.start >= currentTime);
  if (allEpisodesAfterNow.length > currentWeekDraftTimelines.length) {
    const indexOfFirstEntry = allEpisodesAfterNow.indexOf(currentWeekDraftTimelines[0]);

    // Used to splice the array and avoid modifying the draftTimelines one
    const tempDraftList = [...allEpisodesAfterNow];
    firstPart = tempDraftList.splice(0, indexOfFirstEntry);
  }

  let secondPart: ITimeline[] = [];

  // Current entry to evaluate
  let currDraft: ITimeline | undefined = currentWeekDraftTimelines.pop();
  if (!!currDraft) {
    // Keep track the entry with its original start value before change
    let currOriginalStartdate = currDraft.start;
    while (currentWeekDraftTimelines.length > 0 && !!currDraft) {
      const prevDraft = currentWeekDraftTimelines.pop();
      if (prevDraft) {
        // Setting 0 milisec but not modify the values
        const draftDiff = (toSec(currOriginalStartdate) - toSec(prevDraft.stop)) * 1000;

        // Set the original start date before it changes
        currOriginalStartdate = prevDraft.start;

        secondPart.push({...currDraft});
        // Check if gap duration is less than 30min
        if (draftDiff > 0 && draftDiff <= 30 * 60 * 1000) {
          secondPart = moveTimesUp(secondPart, prevDraft.stop).reverse();
        }

        currDraft = {...prevDraft};
      }
    }

    secondPart.push({...currDraft});
  }

  const allDraftTimelines = [...episodesBeforeNow, ...(firstPart || []), ...secondPart].sort((a, b) =>
    a.start.getTime() > b.start.getTime() ? 1 : -1,
  );
  return allDraftTimelines;
};

export const createTimelinesWorker = (
  timeframes: ITimeframe[],
  precedingTimelineItems: (ITimeline | undefined)[],
  queue: (IChannelCatalogItem & {
    state: string;
    stateMsgList: IStateMsgList[];
  })[],
): Promise<ITimeline[]> =>
  worker(
    (
      timeframes: ITimeframe[],
      precedingTimelineItems: ITimeline | undefined,
      queue: IChannelCatalogItemWithState[],
    ): ITimeline[] => {
      // These helpers are inlined so they're available when the worker stringifies
      // this callback and recreates it via Function() in a Web Worker context.
      const _canUseEpisode = (ep: any, start: Date): boolean =>
        ep.hasLinearAvailabilityWindows &&
        ep.allClipsReady &&
        ep.isAvailableNow &&
        !!(ep.availabilityWindows?.linear || []).find(
          (w: any) => start >= new Date(w.startDate) && start <= new Date(w.endDate),
        );

      const _createTimeline = (episode: any, start: Date, uniqueIdPart: number) => {
        const stopTime = new Date(start.getTime() + episode.allotment * 1000);
        return {
          id: `${episode.id}-${uniqueIdPart}`,
          start: start,
          stop: stopTime,
          episode: episode,
          state: episode.state,
          stateMsgList: episode.stateMsgList,
          isRecurrenceEvent: false,
        };
      };

      if (!queue || queue.length === 0) return [];

      const numItems = queue.length;
      const filledTimelines: ITimeline[] = [];

      let timelineIdCounter = Math.abs(Math.random() * 10000);

      timeframes.forEach((timeframe, timeframeIdx) => {
        let nextQueueIndex = 0;
        const precedingEp = (precedingTimelineItems || [])[timeframeIdx];
        const lastUsedIndex = queue.findIndex(i => i.id === precedingEp?.episode.id);
        if (lastUsedIndex !== -1) {
          nextQueueIndex = (lastUsedIndex + 1) % numItems;
        }

        // Truncate ms on the instant (epoch), not via setMilliseconds(), which would
        // shift a time in the DST fall-back repeated hour back 1h. (Runs in a Web Worker,
        // so normalizeDate is not available here.)
        let currentStart = new Date(Math.floor(timeframe.start.getTime() / 1000) * 1000);
        const timeframeEnd = timeframe.stop;
        const timeframeDurationMs = timeframeEnd.getTime() - currentStart.getTime();

        let exactFitPlaced = false;

        // EXACT FILL - Search circularly for the first available episode with exact duration as the gap
        if (timeframeDurationMs > 0) {
          let exactFitCandidateIndex = -1;
          let searchCount = 0;
          let searchIndex = nextQueueIndex;

          while (searchCount < numItems) {
            const potentialEpisode = queue[searchIndex];
            const potentialDurationMs = potentialEpisode.allotment * 1000;

            if (potentialDurationMs === timeframeDurationMs) {
              if (_canUseEpisode(potentialEpisode, currentStart)) {
                exactFitCandidateIndex = searchIndex;
                break;
              }
            }
            searchIndex = (searchIndex + 1) % numItems;
            searchCount++;
          }

          if (exactFitCandidateIndex !== -1) {
            const episodeToAssign = queue[exactFitCandidateIndex];
            const timeline = _createTimeline(episodeToAssign, currentStart, timelineIdCounter++);
            filledTimelines.push(timeline);
            nextQueueIndex = (exactFitCandidateIndex + 1) % numItems;
            exactFitPlaced = true;
            currentStart = new Date(timeframeEnd.getTime());
          }
        }

        // SEQUENTIAL FILL - Attempt to fill the remaining space with episodes in sequence
        let cycleDetectionIndex = -1;

        while (!exactFitPlaced && currentStart.getTime() < timeframeEnd.getTime()) {
          const remainingDurationMs = timeframeEnd.getTime() - currentStart.getTime();

          if (remainingDurationMs <= 0) break;

          let foundFittingItem = false;
          let itemsCheckedInCycle = 0;
          let searchIndex = nextQueueIndex;

          if (cycleDetectionIndex === -1) {
            cycleDetectionIndex = nextQueueIndex;
          }

          while (itemsCheckedInCycle < numItems) {
            const episodeToAssign = queue[searchIndex];
            const episodeDurationMs = episodeToAssign.allotment * 1000;

            if (
              episodeDurationMs > 0 &&
              episodeDurationMs <= remainingDurationMs &&
              _canUseEpisode(episodeToAssign, currentStart)
            ) {
              const timeline = _createTimeline(episodeToAssign, currentStart, timelineIdCounter++);
              filledTimelines.push(timeline);
              nextQueueIndex = (searchIndex + 1) % numItems;
              currentStart = timeline.stop;
              foundFittingItem = true;
              cycleDetectionIndex = -1;
              break;
            }

            searchIndex = (searchIndex + 1) % numItems;
            itemsCheckedInCycle++;

            if (searchIndex === cycleDetectionIndex && !foundFittingItem) {
              break;
            }
          }

          if (!foundFittingItem) {
            break;
          }
        }
      });

      return filledTimelines;
    },
    timeframes,
    precedingTimelineItems,
    queue,
  );

export const getLiveBroadcast = (
  episodes: {
    id: string;
    hasLiveBroadcast: boolean;
  }[],
  episodeId: string,
): boolean => {
  const match = episodes.find(item => item.id === episodeId);
  const hasLiveBroadcast = match?.hasLiveBroadcast ?? false;
  return hasLiveBroadcast;
};
